// SuccessCelebration.js - Animated success screen with confetti
import React, { useEffect, useState } from 'react';
import confetti from 'canvas-confetti';
import { CheckCircle2 } from 'lucide-react';

export function SuccessCelebration({
  title = "You're checked in!",
  subtitle = '',
  onComplete,
  autoDismissMs = 2000
}) {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    // Trigger confetti burst
    const duration = 2000;
    const end = Date.now() + duration;

    // Confetti colors: maroon, white, gold
    const colors = ['#8B1538', '#ffffff', '#fbbf24'];

    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: colors
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: colors
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };

    frame();

    // Also fire a burst from center
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: colors
    });

    // Auto dismiss
    const timer = setTimeout(() => {
      setIsVisible(false);
      onComplete?.();
    }, autoDismissMs);

    return () => clearTimeout(timer);
  }, [autoDismissMs, onComplete]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md">
      <div className="flex flex-col items-center gap-6 p-8">
        {/* Animated checkmark */}
        <div className="animate-success-pop">
          <div className="w-28 h-28 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-2xl shadow-emerald-500/50">
            <CheckCircle2 className="w-16 h-16 text-white" strokeWidth={2.5} />
          </div>
        </div>

        {/* Success text */}
        <div className="text-center">
          <h2
            className="text-3xl font-bold text-white mb-2"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            {title}
          </h2>
          {subtitle && (
            <p className="text-lg text-white/80">{subtitle}</p>
          )}
        </div>

        {/* Time indicator */}
        <div className="w-48 h-1 bg-white/20 rounded-full overflow-hidden">
          <div
            className="h-full bg-white/80 rounded-full"
            style={{
              animation: `shrink ${autoDismissMs}ms linear forwards`
            }}
          />
        </div>
      </div>

      {/* CSS for shrink animation */}
      <style>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
}

export default SuccessCelebration;
